import { useState } from "react";
import { format } from "date-fns";
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-solid-svg-icons/faCalendar";

type Preset = "30d" | "90d" | "6m" | "custom";

interface Props {
  start: Date;
  end: Date;
  onChangeStart: (d: Date) => void;
  onChangeEnd: (d: Date) => void;
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

export function DateRangeSelector({
  start,
  end,
  onChangeStart,
  onChangeEnd,
}: Props) {
  const [preset, setPreset] = useState<Preset>("90d");

  const handlePreset = (value: Preset) => {
    setPreset(value);
    const today = new Date();
    switch (value) {
      case "30d":
        onChangeStart(daysAgo(30));
        onChangeEnd(today);
        break;
      case "90d":
        onChangeStart(daysAgo(90));
        onChangeEnd(today);
        break;
      case "6m":
        onChangeStart(daysAgo(180));
        onChangeEnd(today);
        break;
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Select value={preset} onValueChange={(v) => handlePreset(v as Preset)}>
        <SelectTrigger className="w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="30d">Last 30 days</SelectItem>
          <SelectItem value="90d">Last 90 days</SelectItem>
          <SelectItem value="6m">Last 6 months</SelectItem>
          <SelectItem value="custom">Custom</SelectItem>
        </SelectContent>
      </Select>

      {preset === "custom" && (
        <div className="flex items-center gap-2">
          <DatePicker date={start} onChange={onChangeStart} label="Start" />
          <span className="text-muted-foreground">to</span>
          <DatePicker date={end} onChange={onChangeEnd} label="End" />
        </div>
      )}

      {preset !== "custom" && (
        <span className="text-sm text-muted-foreground">
          {format(start, "MMM d, yyyy")} — {format(end, "MMM d, yyyy")}
        </span>
      )}
    </div>
  );
}

function DatePicker({
  date,
  onChange,
  label,
}: {
  date: Date;
  onChange: (d: Date) => void;
  label: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[150px] justify-start text-left font-normal",
            !date && "text-muted-foreground"
          )}
        >
          <FontAwesomeIcon icon={faCalendar} className="mr-2 h-3.5 w-3.5" />
          {date ? format(date, "MMM d, yyyy") : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => d && onChange(d)}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
